export function numberToRoman (value, lowercase = false) {
  if (isNaN(value)) { return NaN; }
  const digits = String(+value).split('');
  const key = ['', 'C', 'CC', 'CCC', 'CD', 'D', 'DC', 'DCC', 'DCCC', 'CM',
    '', 'X', 'XX', 'XXX', 'XL', 'L', 'LX', 'LXX', 'LXXX', 'XC',
    '', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX'];
  let roman = '';
  let i = 3;
  while (i--) { roman = (key[+digits.pop() + (i * 10)] || '') + roman; }

  const result = Array(+digits.join('') + 1).join('M') + roman;
  return lowercase ? result.toLowerCase() : result;
}

export function numberToChar (value, uppercase = false, startingAt = 1) {
  let baseNumber = value - startingAt;
  const reference = 'abcdefghijklmnopqrstuvwxyz';
  const alphabet = uppercase ? reference.toUpperCase() : reference;
  const base = alphabet.length;
  const digits = [];
  const chars = [];

  do {
    const digit = baseNumber % base;
    digits.push(digit);
    baseNumber = Math.floor(baseNumber / base);
  } while (baseNumber-- > 0);

  while (digits.length) {
    chars.push(alphabet[digits.pop()]);
  }

  return chars.join('');
}

export function numberWithMinDigits (value, minDigits) {
  return new Intl.NumberFormat('en', {
    minimumIntegerDigits: minDigits,
    maximumFractionDigits: 0,
    useGrouping: false
  }).format(value);
}

export function transformNumberToStyle (value, style) {
  switch (style) {
    case 'numbers':
      return Number(value).toString();
    case 'numbers-with-two-digits':
      return numberWithMinDigits(value, 2).toString();
    case 'numbers-with-three-digits':
      return numberWithMinDigits(value, 3).toString();
    case 'numbers-with-four-digits':
      return numberWithMinDigits(value, 4).toString();
    case 'letters':
      return numberToChar(value, false);
    case 'capital-letters':
      return numberToChar(value, true);
    case 'roman-numbers':
      return numberToRoman(value);
    case 'roman-numbers-lower':
      return numberToRoman(value).toLowerCase();
    case 'none':
      return value;
  }
}

export function transformStringToNumber (string, style) {
  switch (style) {
    case 'numbers':
    case 'numbers-with-two-digits':
    case 'numbers-with-three-digits':
    case 'numbers-with-four-digits':
    case 'none':
      return Number(string);
    case 'letters':
      return charToNumber(string, false);
    case 'capital-letters':
      return charToNumber(string, true);
    case 'roman-numbers':
      return romanToNumber(string);
    case 'roman-numbers-lower':
      return romanToNumber(string.toUpperCase());
  }
}

export function charToNumber (char, uppercase = false, startingAt = 1) {
  const reference = 'abcdefghijklmnopqrstuvwxyz';
  const alphabet = uppercase ? reference.toUpperCase() : reference;
  const baseChar = uppercase ? char.toUpperCase() : char.toLowerCase();
  const base = alphabet.length;

  const digits = baseChar.split('').map(char => alphabet.indexOf(char)).reverse();
  if (digits.includes(-1)) return -1;

  let result = 0;
  for (let i = digits.length - 1; i >= 0; i--) {
    result = result * base + digits[i] + 1;
  }

  return result + startingAt - 1;
}

export function romanToNumber (string) {
  const table = {
    I: 1,
    V: 5,
    X: 10,
    L: 50,
    C: 100,
    D: 500,
    M: 1000
  };

  let result = 0;
  for (let i = 0; i < string.length; i++) {
    result = table[string[i]] < table[string[i + 1]]
      ? result - table[string[i]]
      : result + table[string[i]];
  }
  return result;
}

function isUpperCase (string) {
  return /^[A-Z]*$/.test(string);
}

function isLowerCase (string) {
  return /^[a-z]*$/.test(string);
}

export function getStringNumberStyle (string) {
  if (!string) return 'none';
  if (!isNaN(string) && string.length === 1) return 'numbers';
  if (!isNaN(string) && string.length > 4) return 'numbers';
  if (!isNaN(string) && string.length === 2) return 'numbers-with-two-digits';
  if (!isNaN(string) && string.length === 3) return 'numbers-with-three-digits';
  if (!isNaN(string) && string.length === 4) return 'numbers-with-four-digits';
  if (isNaN(string) && numberToRoman(romanToNumber(string)) === string) return 'roman-numbers';
  if (isNaN(string) && isLowerCase(string) && numberToRoman(romanToNumber(string.toUpperCase()), true) === string) return 'roman-numbers-lower';
  if (isNaN(string) && isUpperCase(string)) return 'capital-letters';
  return 'letters';
}

export function getStringsNumberStyle (strings) {
  if (!strings || !strings.length) return 'none';

  const styles = strings.map(string => getStringNumberStyle(string));

  if (styles.some(style => style === 'numbers')) return 'numbers';
  if (styles.some(style => style === 'numbers-with-two-digits')) return 'numbers-with-two-digits';
  if (styles.some(style => style === 'numbers-with-three-digits')) return 'numbers-with-three-digits';
  if (styles.some(style => style === 'numbers-with-four-digits')) return 'numbers-with-four-digits';
  if (styles.every(style => style === 'roman-numbers')) return 'roman-numbers';
  if (styles.every(style => style === 'roman-numbers-lower')) return 'roman-numbers-lower';
  if (styles.some(style => style === 'none')) return 'none';
  if (styles.some(style => style === 'capital-letters')) return 'capital-letters';

  return 'letters';
}

export function getLargestNumberFromObjectKey (target) {
  return Math.max(...Object.keys(target)
    .map(Number));
}

export function fromUnicodeToString (unicode) {
  return String.fromCodePoint(Number(unicode.replace('U+', '0x')));
}

export function fromCharCodeToString (charCode) {
  return String.fromCharCode(charCode);
}
